import { INSPECTED_WITHIN_DAYS } from '../UrlInspectionWindow';

/**
 * Backend-provided GSC metric state. Switch on this — never infer it from null metrics.
 *
 * - `metrics_recorded` — GSC has metrics for the URL in the period.
 * - `zero_in_period` — previously observed in GSC, no metrics this period (confirmed 0).
 * - `no_gsc_history` — never observed in retained GSC data (render as `—`, not 0).
 * - `null` — the row's listing source does not own GSC metrics (render as `—`; it is not a GSC claim).
 */
export type PagesMetricState = 'metrics_recorded' | 'zero_in_period' | 'no_gsc_history' | null;

/** Distinct from a confirmed 0. */
export const NO_VALUE = '—';

/**
 * Shared across every cell (and the inspect drawer) that renders Prerender-side
 * data for a URL with no crawl in the window — same wording everywhere so a
 * customer sees one explanation for the `—`, not several.
 *
 * Load-bearing hedge — SearchPerformance/AGENTS.md: bucket-3 wording is constrained.
 */
export const NOT_PROXIED_TOOLTIP =
  'Google Search Console reports on this URL, with no Prerender crawl observed for it in this period, so Prerender has no crawl date and no served status for it. It does not mean Prerender never served this URL — a crawl before this window would look the same.';

/** Confirmed zeros render as `0`; `no_gsc_history`, an unowned state or a null value render as `—`. */
export function formatMetricCount(value: number | null, metricState: PagesMetricState): string {
  if (metricState === 'no_gsc_history' || metricState === null || value === null) return NO_VALUE;

  return value.toLocaleString();
}

export const NOT_INSPECTED = `Not inspected in the last ${INSPECTED_WITHIN_DAYS} days`;

export type PagesInspection = {
  coverageState: string | null;
  inspectedAt: string | null;
  metricState: PagesMetricState;
};

export function formatInspectionState({ coverageState, inspectedAt, metricState }: PagesInspection): string {
  if (coverageState !== null) return coverageState;
  if (inspectedAt !== null || metricState === 'no_gsc_history' || metricState === null) return NO_VALUE;

  return NOT_INSPECTED;
}

export function formatCtr(ctrPct: number | null): string {
  return ctrPct === null ? NO_VALUE : `${ctrPct}%`;
}

export type PagesTagColor = 'success' | 'warning' | 'error' | 'default';

/**
 * 2xx success · 3xx warning · 4xx/5xx error · else default.
 * Near-duplicate of `statusCodeTagColor` in ai-insights/discoverability (that one has no
 * neutral branch); promote to `src/library` if a third caller appears.
 */
export function httpStatusTagColor(status: number): PagesTagColor {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 300 && status < 400) return 'warning';
  if (status >= 400 && status < 600) return 'error';
  return 'default';
}
